// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// WRPF Pesaje: detects when the weighed-in bodyweight moves a lifter out of the weight class they
// registered in on the WRPF website (`intendedWeightClassKg`), e.g. registered -75 and weighed
// 75.01 -> -82.5. The lifter competes in the weighed class; this only makes the change visible.

import { displayWeight } from "./units";

import { Entry, Language, Sex } from "../types/dataTypes";
import { MeetState } from "../types/stateTypes";

export type WeightClassChange = {
  registered: string; // WRPF website label, e.g. "-75", "-67,5", "+140".
  weighed: string;
  direction: "up" | "down";
};

type MeetClasses = Pick<MeetState, "weightClassesKgMen" | "weightClassesKgWomen" | "weightClassesKgMx">;

const classesForSex = (sex: Sex, meet: MeetClasses): ReadonlyArray<number> => {
  if (sex === "F") return meet.weightClassesKgWomen;
  if (sex === "Mx") return meet.weightClassesKgMx;
  return meet.weightClassesKgMen;
};

// Index of the class a weight falls into (upper bound inclusive, like getWeightClassStr).
// `classes.length` stands for the open class above the top limit.
const classIndexForKg = (classes: ReadonlyArray<number>, kg: number): number => {
  for (let i = 0; i < classes.length; i++) {
    if (kg <= classes[i]) return i;
  }
  return classes.length;
};

// The website exports the registered class as its limit ("75", "67.5", "82.5") and as "+140"/"+90"
// for the open classes. Bundles from before October 2026 floored the half-kilo limits ("67" for
// -67.5, "82" for -82.5) and meets loaded from them keep those labels. Vanilla files may use "140+".
const registeredClassIndex = (label: string, classes: ReadonlyArray<number>): number | null => {
  const trimmed = label.trim();
  if (trimmed === "") return null;
  if (trimmed.startsWith("+") || trimmed.endsWith("+")) return classes.length;
  const kg = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(kg) || kg <= 0) return null;
  return classIndexForKg(classes, kg);
};

// Same labels as the WRPF website ("-75", "-110", "+90", "+140"), with the language's decimal
// separator like every other weight in OpenLifter ("-67,5" in Spanish).
const classLabel = (classes: ReadonlyArray<number>, index: number, language?: Language): string => {
  if (index < classes.length) return "-" + displayWeight(classes[index], language);
  return "+" + displayWeight(classes[classes.length - 1], language);
};

// WRPF label of the weighed-in class; before the weigh-in, the registered one. "" when unknown.
export const getWrpfWeightClassLabel = (
  entry: Pick<Entry, "sex" | "bodyweightKg" | "intendedWeightClassKg">,
  meet: MeetClasses,
  language?: Language,
): string => {
  const classes = classesForSex(entry.sex, meet);
  if (classes.length === 0) return "";
  const index =
    entry.bodyweightKg > 0
      ? classIndexForKg(classes, entry.bodyweightKg)
      : registeredClassIndex(entry.intendedWeightClassKg, classes);
  return index === null ? "" : classLabel(classes, index, language);
};

export const getWeightClassChange = (
  entry: Pick<Entry, "sex" | "bodyweightKg" | "intendedWeightClassKg">,
  meet: MeetClasses,
  language?: Language,
): WeightClassChange | null => {
  if (!(entry.bodyweightKg > 0)) return null;
  const classes = classesForSex(entry.sex, meet);
  if (classes.length === 0) return null;

  const registered = registeredClassIndex(entry.intendedWeightClassKg, classes);
  if (registered === null) return null;
  const weighed = classIndexForKg(classes, entry.bodyweightKg);
  if (weighed === registered) return null;

  return {
    registered: classLabel(classes, registered, language),
    weighed: classLabel(classes, weighed, language),
    direction: weighed > registered ? "up" : "down",
  };
};
